(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initHeader = () => {
    const header = qs('[data-cgs-header]');
    if (!header || header.dataset.ready === 'true') return;
    header.dataset.ready = 'true';

    let queued = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 16);
      queued = false;
    };

    window.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    }, { passive: true });

    qsa('[data-drawer-link]', header).forEach((link) => {
      link.addEventListener('click', () => {
        const details = link.closest('details');
        if (details) {
          details.open = false;
          details.classList.remove('is-product-panel');
        }
      });
    });

    const drawer = qs('.v4-drawer', header);
    const productPanel = qs('[data-product-drawer-panel]', header);
    const drawerMain = qs('[data-drawer-main]', header);
    const productOpen = qs('[data-product-drawer-open]', header);
    const productBack = qs('[data-product-drawer-back]', header);
    const setProductPanel = (open) => {
      if (!drawer || !productPanel || !productOpen) return;
      drawer.classList.toggle('is-product-panel', open);
      productOpen.setAttribute('aria-expanded', String(open));
      productPanel.setAttribute('aria-hidden', String(!open));
      productPanel.toggleAttribute('inert', !open);
      drawerMain?.toggleAttribute('inert', open);
      if (open) {
        window.setTimeout(() => productBack?.focus({ preventScroll: true }), reduceMotion() ? 0 : 180);
      } else if (drawer.open) {
        productOpen.focus({ preventScroll: true });
      }
    };

    productOpen?.addEventListener('click', () => setProductPanel(true));
    productBack?.addEventListener('click', () => setProductPanel(false));
    drawer?.addEventListener('toggle', () => {
      if (!drawer.open) setProductPanel(false);
    });

    qsa('[data-product-guide-link]', header).forEach((link) => {
      link.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
        event.preventDefault();
        window.location.assign(link.href);
      });
    });

    qsa('[data-drawer-close]', header).forEach((button) => {
      button.addEventListener('click', () => {
        const details = button.closest('details');
        setProductPanel(false);
        if (details) details.open = false;
      });
    });

    update();
  };

  const initLocalization = (root = document) => {
    qsa('[data-localization-select]', root).forEach((select) => {
      if (select.dataset.ready === 'true') return;
      select.dataset.ready = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
  };

  const money = (cents, currency) => {
    try {
      const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol'
      }).format(Number(cents) / 100);
      return `${formatted} ${currency}`;
    } catch (error) {
      return `${(Number(cents) / 100).toFixed(2)} ${currency}`;
    }
  };

  const displayCurrency = () => {
    const countrySelect = qs('[data-localization-select][name="country_code"]');
    return countrySelect?.selectedOptions[0]?.dataset.displayCurrency || window.Shopify?.currency?.active || 'USD';
  };

  const convertBucksPrice = (element) => {
    const cents = Number(element.dataset.moneyCents);
    const sourceCurrency = element.dataset.moneyCurrency || window.Shopify?.currency?.active || 'USD';
    const targetCurrency = displayCurrency();
    if (!Number.isFinite(cents)) return;

    let amount = cents / 100;
    if (sourceCurrency !== targetCurrency) {
      const convert = window.bucksCC?.Currency?.convert;
      if (typeof convert !== 'function') return;
      amount = convert(amount, sourceCurrency, targetCurrency);
      if (!Number.isFinite(amount)) return;
    }

    element.textContent = money(amount * 100, targetCurrency);
    element.dataset.displayCurrency = targetCurrency;
  };

  const initBucksPrices = (root = document) => {
    qsa('[data-bucks-price]', root).forEach(convertBucksPrice);
  };

  const initCollectionControls = (root = document) => {
    qsa('[data-collection-controls]', root).forEach((section) => {
      if (section.dataset.collectionControlsReady === 'true') return;
      section.dataset.collectionControlsReady = 'true';

      const sortSelect = qs('[data-collection-sort]', section);
      const viewButtons = qsa('[data-collection-view]', section);
      const storageKey = `cgs-v4-collection-view-${section.dataset.collectionHandle || 'default'}`;

      const refreshLayout = () => {
        window.requestAnimationFrame(() => window.ScrollTrigger?.refresh?.());
      };

      const setView = (view, persist = true) => {
        const nextView = view === 'list' ? 'list' : 'grid';
        section.dataset.collectionLayout = nextView;
        viewButtons.forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.collectionView === nextView));
        });
        if (persist) {
          try {
            window.localStorage.setItem(storageKey, nextView);
          } catch (error) {
          }
        }
        refreshLayout();
      };

      let storedView = 'grid';
      try {
        storedView = window.localStorage.getItem(storageKey) || 'grid';
      } catch (error) {
        storedView = 'grid';
      }
      setView(storedView, false);

      viewButtons.forEach((button) => {
        button.addEventListener('click', () => setView(button.dataset.collectionView));
      });

      sortSelect?.addEventListener('change', () => {
        const url = new URL(window.location.href);
        if (!sortSelect.value || sortSelect.value === sortSelect.dataset.defaultSort) {
          url.searchParams.delete('sort_by');
        } else {
          url.searchParams.set('sort_by', sortSelect.value);
        }
        url.searchParams.delete('page');
        window.location.assign(url.toString());
      });
    });
  };

  const suppressBucksWidget = () => {
    qsa('.buckscc-floating-box, .buckscc-currency-wrapper').forEach((element) => element.remove());
  };

  const initProductForms = (root = document) => {
    qsa('[data-product-form]', root).forEach((form) => {
      if (form.dataset.ready === 'true') return;
      form.dataset.ready = 'true';
      const select = qs('[data-variant-select]', form);
      const data = qs('[data-product-json]', form);
      const productRoot = form.closest('[data-product-root]') || form;
      const prices = qsa('[data-product-price]', productRoot);
      const addButtons = qsa('[data-add-button]', productRoot);
      const checkoutButtons = qsa('[data-card-checkout]', productRoot);
      const buttons = [...addButtons, ...checkoutButtons];
      const optionGroups = qsa('[data-product-option]', form);
      const summary = qs('[data-variant-summary]', form);
      if (!select || !data || !buttons.length) return;

      let variants = [];
      try {
        variants = JSON.parse(data.textContent);
      } catch (error) {
        return;
      }

      const activateGallery = (key) => {
        if (!key) return;
        qsa('[data-product-gallery-panel]', productRoot).forEach((panel) => {
          const active = panel.dataset.productGalleryPanel === key;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
        qsa('[data-product-gallery-trigger]', productRoot).forEach((button) => {
          const active = button.dataset.productGalleryTrigger === key;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      };

      const currentOptionValues = () => optionGroups.map((group) => {
        const pressed = qs('[data-option-value][aria-pressed="true"]', group) || qs('[data-option-value]', group);
        return pressed?.dataset.optionValue;
      });

      const sizeKey = (variant) => {
        return variant ? 'sizes' : null;
      };

      const update = (galleryKey) => {
        const variant = variants.find((item) => String(item.id) === select.value);
        if (!variant) return;
        buttons.forEach((button) => {
          button.disabled = !variant.available;
          const buttonLabel = qs('[data-add-label]', button);
          if (buttonLabel) buttonLabel.textContent = variant.available ? form.dataset.addLabel : form.dataset.soldLabel;
        });
        prices.forEach((price) => {
          price.dataset.moneyCents = String(variant.price);
          price.dataset.moneyCurrency = form.dataset.currency;
          price.textContent = money(variant.price, form.dataset.currency);
        });
        if (summary) summary.textContent = variant.public_title || variant.title;
        if (galleryKey) activateGallery(galleryKey);
        initBucksPrices(productRoot);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      };

      optionGroups.forEach((group) => {
        qsa('[data-option-value]', group).forEach((button) => {
          button.addEventListener('click', () => {
            qsa('[data-option-value]', group).forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
            const chosen = currentOptionValues();
            const variant = variants.find((item) => item.options?.every((value, index) => String(value) === String(chosen[index])));
            if (!variant) return;
            select.value = String(variant.id);
            update(Number(group.dataset.optionPosition) === 2 ? sizeKey(variant) : undefined);
          });
        });
      });

      qsa('[data-product-gallery-trigger]', productRoot).forEach((button) => {
        button.addEventListener('click', () => activateGallery(button.dataset.productGalleryTrigger));
      });

      qsa('[data-size-jump]', productRoot).forEach((button) => {
        button.addEventListener('click', () => {
          const targetSize = button.dataset.sizeJump;
          const sizeGroup = optionGroups.find((group) => Number(group.dataset.optionPosition) === 2);
          if (!sizeGroup) return;
          const option = qsa('[data-option-value]', sizeGroup).find((item) => String(item.dataset.optionValue).startsWith(targetSize));
          option?.click();
          productRoot.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
        });
      });

      checkoutButtons.forEach((button) => {
        button.addEventListener('click', async () => {
          if (button.disabled) return;
          const originalDisabled = button.disabled;
          button.disabled = true;
          button.classList.add('is-loading');
          const rootPath = window.Shopify?.routes?.root || '/';
          try {
            const formData = new FormData(form);
            const response = await fetch(`${rootPath}cart/add.js`, {
              method: 'POST',
              body: formData,
              headers: { Accept: 'application/json' },
            });
            if (!response.ok) throw new Error('Cart add failed');
            window.location.href = button.dataset.checkoutUrl || `${rootPath}checkout`;
          } catch (error) {
            button.disabled = originalDisabled;
            button.classList.remove('is-loading');
            form.requestSubmit();
          }
        });
      });

      select.addEventListener('change', () => {
        const variant = variants.find((item) => String(item.id) === select.value);
        if (!variant) return;
        optionGroups.forEach((group, index) => {
          qsa('[data-option-value]', group).forEach((button) => {
            button.setAttribute('aria-pressed', String(String(button.dataset.optionValue) === String(variant.options?.[index])));
          });
        });
        update(sizeKey(variant));
      });
      update();
    });
  };

  const initAeroExperience = (root = document) => {
    qsa('[data-product-root]', root).forEach((productRoot) => {
      if (productRoot.dataset.aeroReady === 'true') return;
      productRoot.dataset.aeroReady = 'true';

      qsa('[data-hardness-demo]', productRoot).forEach((demo) => {
        const response = qs('.v4-aero-base__response', demo);
        qsa('[data-hardness]', demo).forEach((button) => {
          button.addEventListener('click', () => {
            qsa('[data-hardness]', demo).forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
            const soft = button.dataset.hardness === 'soft';
            response?.style.setProperty('--aero-compression', soft ? '0.38' : '0.78');
            response?.style.setProperty('--aero-pressure', soft ? '3.8rem' : '2.1rem');
          });
        });
      });
    });
  };

  const initAeroFilm = (root = document) => {
    qsa('[data-aero-film-toggle]', root).forEach((button) => {
      if (button.dataset.ready === 'true') return;
      button.dataset.ready = 'true';
      const stage = button.closest('.v4-aero-film__stage');
      const visual = qs('[data-aero-film-visual]', stage);
      if (!visual) return;

      const setPaused = (paused) => {
        if (paused) {
          visual.pause?.();
        } else {
          visual.play?.().catch(() => {});
        }
        button.setAttribute('aria-pressed', String(paused));
        button.textContent = paused ? button.dataset.playLabel : button.dataset.pauseLabel;
      };

      if (reduceMotion()) setPaused(true);
      button.addEventListener('click', () => {
        setPaused(button.getAttribute('aria-pressed') !== 'true');
      });
    });
  };

  let motionMedia;
  const initMotion = () => {
    if (!window.gsap || !window.ScrollTrigger) return;
    if (document.body.dataset.motionEnabled !== 'true' || reduceMotion()) {
      qsa('[data-cgs-reveal]').forEach((item) => window.gsap.set(item, { clearProps: 'all' }));
      return;
    }

    motionMedia?.revert();
    window.gsap.registerPlugin(window.ScrollTrigger);
    motionMedia = window.gsap.matchMedia();

    motionMedia.add({
      desktop: '(min-width: 990px)',
      mobile: '(max-width: 989px)',
      finePointer: '(pointer: fine)'
    }, (context) => {
      const { desktop, mobile, finePointer } = context.conditions;
      const cleanups = [];

      qsa('[data-cgs-hero]').forEach((hero, index) => {
        const timeline = window.gsap.timeline({
          defaults: { duration: 0.9, ease: 'power3.out' },
          id: `cgs-v4-hero-${index}`
        });
        timeline
          .from(qsa('[data-hero-meta]', hero), { y: 18, autoAlpha: 0, stagger: 0.08 }, 0.1)
          .from(qsa('[data-hero-line]', hero), { yPercent: 110, rotation: 1.5, stagger: 0.1 }, 0.18)
          .from(qsa('[data-hero-copy]', hero), { y: 24, autoAlpha: 0, stagger: 0.08 }, 0.45)
          .from(qs('[data-hero-product]', hero), { xPercent: desktop ? 18 : 0, yPercent: mobile ? 8 : 0, scale: 0.88, rotation: 4, autoAlpha: 0 }, 0.28)
          .from(qsa('[data-hero-spec]', hero), { y: 20, autoAlpha: 0, stagger: 0.08 }, 0.62);

        if (desktop) {
          const product = qs('[data-hero-product]', hero);
          const glow = qs('[data-hero-glow]', hero);
          if (product) {
            window.gsap.to(product, {
              yPercent: -8,
              rotation: -1.5,
              ease: 'none',
              scrollTrigger: {
                id: `cgs-v4-hero-parallax-${index}`,
                trigger: hero,
                start: 'top top',
                end: 'bottom top',
                scrub: 0.8
              }
            });
          }
          if (glow) {
            window.gsap.to(glow, {
              xPercent: 18,
              yPercent: -12,
              ease: 'none',
              scrollTrigger: {
                id: `cgs-v4-hero-glow-${index}`,
                trigger: hero,
                start: 'top top',
                end: 'bottom top',
                scrub: 1.2
              }
            });
          }
        }
      });

      qsa('[data-cgs-reveal]').forEach((element, index) => {
        window.gsap.from(element, {
          y: mobile ? 28 : 48,
          autoAlpha: 0,
          duration: 0.85,
          ease: 'power3.out',
          scrollTrigger: {
            id: `cgs-v4-reveal-${index}`,
            trigger: element,
            start: 'top 88%',
            once: true
          }
        });
      });

      qsa('[data-cgs-story]').forEach((story, index) => {
        const stage = qs('[data-story-stage]', story);
        const track = qs('[data-story-track]', story);
        if (!stage || !track) return;

        if (desktop) {
          const distance = () => Math.max(0, track.scrollWidth - stage.clientWidth);
          window.gsap.to(track, {
            x: () => -distance(),
            ease: 'none',
            scrollTrigger: {
              id: `cgs-v4-story-${index}`,
              trigger: story,
              start: 'top top',
              end: () => `+=${Math.max(window.innerHeight, distance())}`,
              pin: stage,
              scrub: 0.8,
              anticipatePin: 1,
              invalidateOnRefresh: true
            }
          });
        } else {
          qsa('[data-story-card]', track).forEach((card, cardIndex) => {
            window.gsap.from(card, {
              y: 36,
              autoAlpha: 0,
              duration: 0.7,
              ease: 'power2.out',
              scrollTrigger: {
                id: `cgs-v4-story-card-${index}-${cardIndex}`,
                trigger: card,
                start: 'top 88%',
                once: true
              }
            });
          });
        }
      });

      qsa('[data-aero-motion]').forEach((section, sectionIndex) => {
        qsa('.v4-aero-intro__meter strong', section).forEach((number, numberIndex) => {
          const target = Number(number.textContent);
          const state = { value: 0 };
          window.gsap.to(state, {
            value: target,
            duration: 1.25,
            ease: 'power3.out',
            snap: { value: 1 },
            onUpdate: () => { number.textContent = String(state.value); },
            scrollTrigger: {
              id: `cgs-aero-meter-${sectionIndex}-${numberIndex}`,
              trigger: section,
              start: 'top 78%',
              once: true
            }
          });
        });
      });

      qsa('[data-aero-grip]').forEach((section, index) => {
        const image = qs('.v4-aero-grip__media img', section);
        if (!image) return;
        window.gsap.fromTo(image, {
          scale: 1.08,
          yPercent: 4
        }, {
          scale: 1,
          yPercent: -3,
          ease: 'none',
          scrollTrigger: {
            id: `cgs-aero-grip-${index}`,
            trigger: section,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.9
          }
        });
      });

      if (finePointer) {
        qsa('[data-tilt-card]').forEach((card) => {
          const media = qs('[data-tilt-media]', card) || card;
          const xTo = window.gsap.quickTo(media, 'rotationY', { duration: 0.5, ease: 'power3.out' });
          const yTo = window.gsap.quickTo(media, 'rotationX', { duration: 0.5, ease: 'power3.out' });
          const onMove = (event) => {
            const rect = card.getBoundingClientRect();
            xTo(((event.clientX - rect.left) / rect.width - 0.5) * 7);
            yTo(((event.clientY - rect.top) / rect.height - 0.5) * -7);
          };
          const onLeave = () => {
            xTo(0);
            yTo(0);
          };
          card.addEventListener('pointermove', onMove);
          card.addEventListener('pointerleave', onLeave);
          cleanups.push(() => {
            card.removeEventListener('pointermove', onMove);
            card.removeEventListener('pointerleave', onLeave);
          });
        });
      }

      return () => cleanups.forEach((cleanup) => cleanup());
    });

    const refresh = () => window.ScrollTrigger.refresh();
    if (document.fonts?.ready) document.fonts.ready.then(refresh);
    qsa('img[loading="eager"]').forEach((image) => {
      if (!image.complete) image.addEventListener('load', refresh, { once: true });
    });
  };

  class AimForge {
    constructor(root) {
      this.root = root;
      this.arena = qs('[data-aim-arena]', root);
      this.target = qs('[data-aim-target]', root);
      this.dialog = qs('[data-aim-results]', root);
      this.duration = Number(root.dataset.duration || 30);
      this.mode = 'flick';
      this.running = false;
      this.hits = 0;
      this.attempts = 0;
      this.reactions = [];
      this.score = 0;
      this.timer = 0;
      this.nextTimer = 0;
      this.audio = true;
      this.bind();
      this.updateBest();
    }

    bind() {
      qsa('[data-aim-start]', this.root).forEach((button) => button.addEventListener('click', () => this.start()));
      qs('[data-aim-restart]', this.root)?.addEventListener('click', () => {
        this.dialog?.close();
        this.start();
      });
      qs('[data-aim-share]', this.root)?.addEventListener('click', () => this.share());
      qs('[data-aim-sound]', this.root)?.addEventListener('click', (event) => {
        this.audio = !this.audio;
        event.currentTarget.setAttribute('aria-pressed', String(this.audio));
      });
      qs('[data-aim-fullscreen]', this.root)?.addEventListener('click', () => {
        if (!document.fullscreenElement) this.root.requestFullscreen?.();
        else document.exitFullscreen?.();
      });
      qsa('[data-aim-mode]', this.root).forEach((button) => {
        button.addEventListener('click', () => {
          if (this.running) return;
          this.mode = button.dataset.aimMode;
          qsa('[data-aim-mode]', this.root).forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
        });
      });
      this.target?.addEventListener('pointerdown', (event) => this.hit(event));
      this.arena?.addEventListener('pointerdown', (event) => {
        if (!this.running || event.target === this.target || this.target?.contains(event.target)) return;
        this.attempts += 1;
        this.updateStats();
      });
    }

    start() {
      window.cancelAnimationFrame(this.timer);
      window.clearTimeout(this.nextTimer);
      this.running = true;
      this.hits = 0;
      this.attempts = 0;
      this.reactions = [];
      this.score = 0;
      this.startedAt = performance.now();
      this.root.dataset.state = 'running';
      this.setText('status', this.root.dataset.running);
      this.updateStats(this.duration);
      this.placeTarget();
      this.tick();
    }

    tick() {
      if (!this.running) return;
      const elapsed = (performance.now() - this.startedAt) / 1000;
      const remaining = Math.max(0, this.duration - elapsed);
      this.updateStats(remaining);
      if (remaining <= 0) {
        this.finish();
        return;
      }
      this.timer = window.requestAnimationFrame(() => this.tick());
    }

    placeTarget() {
      if (!this.running || !this.target || !this.arena) return;
      const rect = this.arena.getBoundingClientRect();
      const modeSize = this.mode === 'precision' ? 42 : this.mode === 'reflex' ? 60 : 52;
      const size = Math.max(42, Math.min(modeSize, rect.width * 0.14));
      const pad = size / 2 + 10;
      const x = pad + Math.random() * Math.max(1, rect.width - pad * 2);
      const y = pad + Math.random() * Math.max(1, rect.height - pad * 2);
      this.target.style.setProperty('--aim-size', `${size}px`);
      this.target.style.left = `${x}px`;
      this.target.style.top = `${y}px`;
      this.target.hidden = false;
      this.targetShownAt = performance.now();
      if (window.gsap && !reduceMotion()) {
        window.gsap.fromTo(this.target, { scale: 0.45, autoAlpha: 0 }, { scale: 1, autoAlpha: 1, duration: 0.28, ease: 'back.out(1.9)', overwrite: true });
      }
    }

    hit(event) {
      if (!this.running) return;
      event.preventDefault();
      event.stopPropagation();
      const reaction = performance.now() - this.targetShownAt;
      this.hits += 1;
      this.attempts += 1;
      this.reactions.push(reaction);
      const modeFactor = this.mode === 'precision' ? 1.3 : this.mode === 'reflex' ? 1.15 : 1;
      this.score += Math.max(40, Math.round((1100 - Math.min(reaction, 1000)) * modeFactor));
      this.beep();
      this.target.hidden = true;
      this.updateStats();
      const delay = this.mode === 'reflex' ? 90 + Math.random() * 420 : 90;
      this.nextTimer = window.setTimeout(() => this.placeTarget(), delay);
    }

    finish() {
      this.running = false;
      this.root.dataset.state = 'complete';
      this.target.hidden = true;
      this.setText('status', this.root.dataset.complete);
      this.updateStats(0);
      const best = Math.max(this.score, Number(window.localStorage.getItem('cgs-v4-aim-best') || 0));
      window.localStorage.setItem('cgs-v4-aim-best', String(best));
      this.updateBest();
      qsa('[data-aim-result]', this.root).forEach((element) => {
        const key = element.dataset.aimResult;
        if (key === 'score') element.textContent = String(this.score);
        if (key === 'hits') element.textContent = String(this.hits);
        if (key === 'accuracy') element.textContent = `${this.accuracy()}%`;
        if (key === 'average') element.textContent = this.average();
      });
      this.dialog?.showModal();
    }

    accuracy() {
      return this.attempts ? Math.round((this.hits / this.attempts) * 100) : 100;
    }

    average() {
      if (!this.reactions.length) return '--';
      return `${Math.round(this.reactions.reduce((sum, value) => sum + value, 0) / this.reactions.length)} ms`;
    }

    updateStats(remaining) {
      const time = remaining === undefined ? Math.max(0, this.duration - (performance.now() - this.startedAt) / 1000) : remaining;
      this.setText('time', time.toFixed(1));
      this.setText('hits', String(this.hits));
      this.setText('accuracy', `${this.accuracy()}%`);
      this.setText('average', this.average());
      this.setText('score', String(this.score));
      this.root.style.setProperty('--aim-progress', `${Math.max(0, Math.min(1, time / this.duration)) * 100}%`);
    }

    updateBest() {
      this.setText('best', window.localStorage.getItem('cgs-v4-aim-best') || '0');
    }

    setText(key, value) {
      qsa(`[data-aim-stat="${key}"]`, this.root).forEach((element) => { element.textContent = value; });
    }

    beep() {
      if (!this.audio || !window.AudioContext) return;
      const context = new window.AudioContext();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = 520 + Math.min(this.hits, 12) * 18;
      gain.gain.setValueAtTime(0.035, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.06);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.06);
      oscillator.addEventListener('ended', () => context.close());
    }

    async share() {
      const text = `Cero Aim Forge: ${this.score} points / ${this.accuracy()}% accuracy / ${this.average()}`;
      try {
        await navigator.clipboard.writeText(text);
      } catch (error) {
        return;
      }
    }
  }

  const initAimForge = (root = document) => {
    qsa('[data-aim-forge]', root).forEach((element) => {
      if (element.dataset.ready === 'true') return;
      element.dataset.ready = 'true';
      new AimForge(element);
    });
  };

  const initialize = (root = document) => {
    initHeader();
    initLocalization(root);
    initBucksPrices(root);
    initCollectionControls(root);
    initProductForms(root);
    initAeroExperience(root);
    initAeroFilm(root);
    initAimForge(root);
  };

  document.addEventListener('DOMContentLoaded', () => {
    initialize();
    initMotion();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    initMotion();
  });

  document.addEventListener('shopify:section:reorder', initMotion);
  window.addEventListener('BUCKSCC-LOADED', () => {
    suppressBucksWidget();
    initBucksPrices();
  });
  window.addEventListener('load', () => initBucksPrices());
})();
